// @ts-check
// =============================================================================
// js/screens/challenge-ladder.js — single-player: pick a bot, pick a tier.
//
// WHAT THIS IS (plan/challenge-ladder.md; old DuelBotPopup :977)
//   The single-player progression surface. Each bot has a 1–20 tier ladder
//   per its hero: playing "with tier" gives the bot a scaling head start
//   (server BotStart) and beating your current tier unlocks the next
//   (app/progression.js, saved locally like the old client).
//
// FLOW
//   Pick a bot -> optionally enable "Play with Tier" + pick an eligible tier
//   -> Start Duel -> request "Bot Game" {deckIndex, botIndex, botName, tier}
//   -> the returned duel routes into #/duel; the bot-runner drives the bot's
//   moves between your turns.
// =============================================================================

import { store, setSlice } from '../core/store.js';
import { navigate } from '../core/router.js';
import { request } from '../net/http.js';
import { can } from '../app/session.js';
import { callStatsAPI } from '../app/analytics.js';
import { loadChallengeLevels } from '../app/progression.js';
import { loadBots, availableBots } from '../net/bot-runner.js';
import { HERO_ASSET_NAMES, selectDeck } from '../ui/deck-list.js';
import { showUpsell } from '../ui/upsell.js';
import { escapeHtml } from '../ui/modal.js';
import { toast } from '../ui/toast.js';
import { requestGameFullscreen, exitGameFullscreen } from '../ui/fullscreen.js';

/** Selected bot id (persists within the session). */
let selectedBotId = 'BoringHuman_auri';

/** "Play with Tier" toggle + chosen tier. */
let tierEnabled = false;
let currentTier = 1;

/** @type {HTMLElement|null} */
let screenEl = null;

/**
 * Render the challenge ladder.
 * @param {HTMLElement} container
 * @param {Record<string,string>} _params
 * @returns {void}
 */
export function render(container, _params) {
	screenEl = document.createElement('div');
	screenEl.className = 'screen';
	container.appendChild(screenEl);

	if (!can('duelBot')) {
		screenEl.innerHTML = `<h1 class="screen-title">Duel Bot</h1>`;
		showUpsell('host');
		return;
	}

	// Entering directly (splash "Duel A Bot") may skip the lobby's deck
	// fallback — default to the first prebuilt so Start Duel just works.
	if (!store.loadedDeck?.list) {
		const fallback = store.userData?.defaultdecks?.[0];
		if (fallback?.list) {
			selectDeck(fallback, fallback.index);
		}
	}

	screenEl.innerHTML = `<h1 class="screen-title">Duel Bot</h1>
		<div class="empty-state">Loading bots…</div>`;
	void loadBots().then(() => paint());
}

/** Nothing persistent to tear down. */
export function destroy() {
	screenEl = null;
}

/** Repaint the roster + tier picker. */
function paint() {
	if (!screenEl) return;
	const bots = availableBots();
	if (!bots.length) {
		screenEl.innerHTML = `<h1 class="screen-title">Duel Bot</h1>
			<div class="empty-state">No bots are available right now.</div>`;
		return;
	}
	if (!bots.some((b) => b.id === selectedBotId)) {
		selectedBotId = bots[0].id;
	}
	const selected = bots.find((b) => b.id === selectedBotId) || bots[0];
	const levels = loadChallengeLevels();
	const maxEligible = Math.min(20, Math.max(0, levels[selected.hero] || 0));
	currentTier = Math.min(Math.max(1, currentTier), Math.max(1, maxEligible || 1));
	// New players (no tier unlocked against this bot yet) don't see the tier
	// ladder at all — it's noise until they've earned a rung. Beating an
	// untiered game unlocks tier 1 and the section appears. With nothing
	// unlocked the toggle also can't linger enabled from another bot.
	const showTiers = maxEligible > 0;
	if (!showTiers) tierEnabled = false;

	const intro = showTiers
		? 'Choose a bot. Optionally enable a challenge tier and climb the ladder — beating your top tier unlocks the next.'
		: 'Choose a bot to duel.';
	let html = `<h1 class="screen-title">Duel Bot</h1>
		<div class="ladder-intro">${intro}</div>
		<div class="ladder-grid">`;

	for (const bot of bots) {
		const heroName = HERO_ASSET_NAMES[bot.hero];
		const unlocked = Math.max(0, levels[bot.hero] || 0);
		html += `
			<div class="bot-card${bot.id === selectedBotId ? ' bot-card--selected' : ''}" data-bot="${bot.id}">
				<img src="/leaders/faces/${heroName}.png" alt="${escapeHtml(bot.name)}">
				<div class="bot-card-info">
					<div class="bot-card-name">${escapeHtml(bot.name)}</div>
					<div class="bot-card-sub">${unlocked ? `Ladder: tier ${unlocked}/20` : 'Ladder locked — win once to start'}</div>
				</div>
			</div>`;
	}
	html += `</div>`;

	// Tier toggle + chips (only tiers you've earned are pickable) — shown only
	// once at least one tier is unlocked against the selected bot.
	if (showTiers) {
		html += `
			<div class="ladder-tier-section">
				<label class="ladder-tier-toggle">
					<input id="enable-tier" type="checkbox" ${tierEnabled ? 'checked' : ''}>
					<span>Play with Tier (up to ${maxEligible})</span>
				</label>
				<div id="tier-chips" class="ladder-chips${tierEnabled ? '' : ' is-disabled'}">`;
		for (let tier = 1; tier <= 20; tier++) {
			const eligible = tier <= maxEligible;
			html += `<button class="ladder-chip${tier === currentTier ? ' active' : ''}"
				data-tier="${tier}" ${eligible ? '' : 'disabled'}>${tier}</button>`;
		}
		html += `</div></div>`;
	}
	html += `
		<div style="text-align:center;margin-top:1rem;">
			<button class="defaultButton" id="btn-start">Start Duel</button>
		</div>`;

	screenEl.innerHTML = html;
	wireEvents(maxEligible);
}

/**
 * @param {number} maxEligible
 */
function wireEvents(maxEligible) {
	if (!screenEl) return;
	for (const card of screenEl.querySelectorAll('.bot-card')) {
		card.addEventListener('click', () => {
			selectedBotId = card.getAttribute('data-bot') || selectedBotId;
			paint();
		});
	}
	screenEl.querySelector('#enable-tier')?.addEventListener('change', (e) => {
		tierEnabled = /** @type {HTMLInputElement} */ (e.target).checked;
		if (tierEnabled && !maxEligible) {
			toast('Win an untiered game against this bot first!');
			tierEnabled = false;
		}
		paint();
	});
	for (const chip of screenEl.querySelectorAll('.ladder-chip:not([disabled])')) {
		chip.addEventListener('click', () => {
			currentTier = Number(chip.getAttribute('data-tier'));
			paint();
		});
	}
	screenEl.querySelector('#btn-start')?.addEventListener('click', () => void startDuel());
}

/** Kick off the bot game (old BotGame :3419). */
async function startDuel() {
	const loadedDeck = store.loadedDeck;
	if (!loadedDeck?.list) {
		toast('Pick a deck in the lobby first!');
		navigate('lobby');
		return;
	}
	// Guests fight the bot with prebuilt decks only (mirror of the server
	// gate, so the failure is explained instead of silent).
	if (store.session.isGuest && !(Number(loadedDeck.index) >= 100)) {
		toast('Guests duel with prebuilt decks — pick one from the lobby!');
		navigate('lobby');
		return;
	}
	const registry = /** @type {any} */ (window).biterbots || {};
	const bot = registry[selectedBotId];
	if (!bot) {
		toast('That bot failed to load.', { error: true });
		return;
	}
	// Mobile: fullscreen off THIS click (after the sync checks above, before
	// the await — the gesture is only guaranteed valid while the handler is on
	// the stack), so the duel mounts with fullscreen already up.
	requestGameFullscreen();
	callStatsAPI('Duel:Bot');
	const tier = tierEnabled ? currentTier : 0;
	const returned = await request('Bot Game', {
		deckIndex: loadedDeck.index,
		botIndex: bot.deckIndex,
		botName: selectedBotId,
		tier,
	});
	if (returned?.deckInvalid) {
		exitGameFullscreen();
		toast(returned.reason || 'That deck is no longer valid — update it in the deck builder.', { error: true });
		return;
	}
	if (!returned || returned.message === 'Fail' || !returned.duel) {
		exitGameFullscreen();
		toast(returned?.reason || 'Could not start the bot game.', { error: true });
		return;
	}
	if (returned.duels) {
		setSlice('lobby', { ...store.lobby, duels: returned.duels });
	}
	setSlice('duel', returned.duel); // boot's duel watcher routes into #/duel
}
